import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Users, Plus, Pencil, Trash2, Shield, User, Warehouse } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { errorMessage } from '../components/LoadError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UserManagementPage = () => {
  const { user: currentUser } = useAuth();
  const isSelf = (email) => Boolean(email && currentUser?.email && email.toLowerCase() === currentUser.email.toLowerCase());
  const [users, setUsers] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // user awaiting delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'manager'
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { name: formData.name, role: formData.role };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(`${API}/users/${editingUser.email}`, updateData, { headers: getAuthHeaders() });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API}/users`, formData, { headers: getAuthHeaders() });
        toast.success('User created successfully');
      }
      setShowForm(false);
      setEditingUser(null);
      setFormData({ email: '', name: '', password: '', role: 'manager' });
      fetchUsers();
    } catch (error) {
      toast.error(editingUser ? 'Could not update user' : 'Could not create user', { description: errorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role
    });
    setShowForm(true);
  };

  const handleDelete = (user) => {
    if (isSelf(user.email)) {
      toast.error("You can't delete your own account");
      return;
    }
    setDeleteTarget(user);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    const email = deleteTarget?.email;
    try {
      await axios.delete(`${API}/users/${email}`, { headers: getAuthHeaders() });
      toast.success(`User ${email} deleted`);
      fetchUsers();
    } catch (error) {
      toast.error(`Failed to delete ${email}`, { description: errorMessage(error) });
      throw error; // keep the dialog open
    }
  };

  // The signed-in admin may not demote themselves (they would lose access to this page).
  const lockOwnRole = Boolean(editingUser && isSelf(editingUser.email) && editingUser.role === 'admin');
  // Never let the company end up with no admin: the last admin's role is fixed.
  const adminCount = users.filter((u) => u.role === 'admin').length;
  const lockLastAdmin = Boolean(editingUser && editingUser.role === 'admin' && adminCount <= 1);

  const closeForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({ email: '', name: '', password: '', role: 'manager' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[15px] text-gray-500">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">User Management</h1>
          <p className="mt-1 text-[15px] text-gray-500">Manage user accounts and access levels</p>
        </div>
        <Button 
          onClick={() => setShowForm(true)} 
          className="h-9 px-4 bg-[#007AFF] hover:bg-[#0066CC] text-white text-[13px] font-medium rounded-lg"
          data-testid="add-user-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* User Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) closeForm(); }}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="user-form-modal">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold text-black" data-testid="user-form-title">
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? `Update ${editingUser.email}. Changing the role or password signs them out.` : 'Create a sign-in for a team member.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="user-form-email" className="mb-1.5 block text-[13px] font-medium text-gray-700">Email</Label>
              <Input
                id="user-form-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!editingUser}
                required
                autoComplete="off"
                className="rounded-lg"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label htmlFor="user-form-name" className="mb-1.5 block text-[13px] font-medium text-gray-700">Name</Label>
              <Input
                id="user-form-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-lg"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label htmlFor="user-form-password" className="mb-1.5 block text-[13px] font-medium text-gray-700">
                {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
              </Label>
              <Input
                id="user-form-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                autoComplete="new-password"
                className="rounded-lg"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label htmlFor="user-form-role" className="mb-1.5 block text-[13px] font-medium text-gray-700">Role</Label>
              <select
                id="user-form-role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                disabled={lockOwnRole || lockLastAdmin}
                aria-describedby={lockOwnRole || lockLastAdmin ? 'user-form-role-hint' : undefined}
                className="flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[14px] disabled:cursor-not-allowed disabled:bg-gray-50"
                data-testid="user-role-select"
              >
                <option value="admin">Admin (Full Access)</option>
                <option value="manager">Manager (Limited Access)</option>
                <option value="warehouse">Warehouse (Purchases, Stock & Returns only)</option>
              </select>
              {lockOwnRole && (
                <p id="user-form-role-hint" className="mt-1 text-[12px] text-gray-600" data-testid="user-role-self-hint">
                  You can't remove your own admin role. Ask another admin to change it.
                </p>
              )}
              {!lockOwnRole && lockLastAdmin && (
                <p id="user-form-role-hint" className="mt-1 text-[12px] text-gray-600" data-testid="user-role-last-admin-hint">
                  This is the only admin. Make someone else an admin first.
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeForm}
                className="rounded-lg"
                data-testid="cancel-user-form-button"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#007AFF] hover:bg-[#0066CC] rounded-lg" data-testid="save-user-btn">
                {saving ? 'Saving…' : `${editingUser ? 'Update' : 'Create'} User`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Users Table */}
      <Card className="bg-white rounded-2xl border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[780px]" role="table" aria-label="GalaxyIQ users" data-testid="users-table">
            <div className="bg-gray-50" role="rowgroup">
              <div className="grid grid-cols-[1.4fr_1.4fr_0.8fr_0.8fr_0.6fr]" role="row">
                <div role="columnheader" className="px-6 py-4 text-left text-[12px] font-semibold text-gray-500 uppercase tracking-wide">User</div>
                <div role="columnheader" className="px-6 py-4 text-left text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Email</div>
                <div role="columnheader" className="px-6 py-4 text-left text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Role</div>
                <div role="columnheader" className="px-6 py-4 text-left text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Created</div>
                <div role="columnheader" className="px-6 py-4 text-right text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Actions</div>
              </div>
            </div>
            <div className="divide-y divide-gray-100" role="rowgroup">
              {users.map((user) => (
                <div
                  key={user.email}
                  className="grid grid-cols-[1.4fr_1.4fr_0.8fr_0.8fr_0.6fr] hover:bg-gray-50 transition-colors"
                  role="row"
                  data-testid={`user-row-${user.email}`}
                >
                  <div className="px-6 py-4" role="cell">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        user.role === 'admin' 
                          ? 'bg-gradient-to-br from-[#007AFF] to-[#5856D6]' 
                          : user.role === 'warehouse'
                          ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                          : 'bg-gray-200'
                      }`}>
                        {user.role === 'admin' ? (
                          <Shield className="h-5 w-5 text-white" />
                        ) : user.role === 'warehouse' ? (
                          <Warehouse className="h-5 w-5 text-white" />
                        ) : (
                          <User className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      <span className="text-[14px] font-medium text-black" data-testid={`user-name-${user.email}`}>{user.name}</span>
                    </div>
                  </div>
                  <div className="px-6 py-4 text-[14px] text-gray-600" role="cell" data-testid={`user-email-${user.email}`}>{user.email}</div>
                  <div className="px-6 py-4" role="cell">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold ${
                      user.role === 'admin' 
                        ? 'bg-blue-100 text-blue-700' 
                        : user.role === 'warehouse'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-600'
                    }`} data-testid={`user-role-${user.email}`}>
                      {user.role === 'admin' ? 'Admin' : user.role === 'warehouse' ? 'Warehouse' : 'Manager'}
                    </span>
                  </div>
                  <div className="px-6 py-4 text-[13px] text-gray-500" role="cell" data-testid={`user-created-${user.email}`}>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                  </div>
                  <div className="px-6 py-4 text-right" role="cell">
                    <div className="flex items-center justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEdit(user)}
                        className="h-8 w-8 rounded-lg hover:bg-gray-100"
                        aria-label={`Edit ${user.email}`}
                        data-testid={`edit-user-${user.email}`}
                      >
                        <Pencil className="h-4 w-4 text-gray-500" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(user)}
                        disabled={isSelf(user.email)}
                        title={isSelf(user.email) ? "You can't delete your own account" : undefined}
                        className="h-8 w-8 rounded-lg hover:bg-red-50"
                        aria-label={isSelf(user.email) ? `Delete ${user.email} (not allowed for your own account)` : `Delete ${user.email}`}
                        data-testid={`delete-user-${user.email}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {users.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Users className="h-10 w-10 mx-auto mb-2 opacity-50" aria-hidden="true" />
            <p className="text-[14px]">No users found</p>
          </div>
        )}
      </Card>

      {/* Role Info */}
      <Card className="p-6 bg-gray-50 rounded-2xl border-0">
        <h3 className="text-[15px] font-semibold text-black mb-4">Role Permissions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 bg-white rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <span className="text-[14px] font-semibold text-black">Admin</span>
            </div>
            <ul className="text-[13px] text-gray-600 space-y-1.5">
              <li>• Full access to all features</li>
              <li>• User management (create, edit, delete)</li>
              <li>• Bulk inventory updates via Excel</li>
              <li>• Can rename a SKU code in SKU Master</li>
              <li>• Settings configuration</li>
            </ul>
          </div>
          <div className="p-5 bg-white rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                <User className="h-4 w-4 text-gray-500" />
              </div>
              <span className="text-[14px] font-semibold text-black">Manager</span>
            </div>
            <ul className="text-[13px] text-gray-600 space-y-1.5">
              <li>• View all data and reports</li>
              <li>• Create and edit SKUs, inventory, sales</li>
              <li>• Manage shipments and POs</li>
              <li>• Bulk imports (sales, Retail POs, SKU master)</li>
              <li>• No access to user management, clearing data, renaming SKU codes or posting purchases</li>
            </ul>
          </div>
          <div className="p-5 bg-white rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Warehouse className="h-4 w-4 text-white" />
              </div>
              <span className="text-[14px] font-semibold text-black">Warehouse</span>
            </div>
            <ul className="text-[13px] text-gray-600 space-y-1.5">
              <li>• Log and edit Purchase invoices (posting still needs an Admin)</li>
              <li>• Update warehouse/bin stock in Inventory</li>
              <li>• Record and grade Returns</li>
              <li>• Everything else (SKU Master, Sales, Retail POs…) is view-only</li>
            </ul>
          </div>
        </div>
      </Card>
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete user ${deleteTarget?.email || ''}?`}
        description={`${deleteTarget?.name ? `${deleteTarget.name} ` : 'This user '}will lose access to GalaxyIQ immediately. This cannot be undone.`}
        confirmLabel="Delete user"
        busyLabel="Deleting…"
        destructive
        onConfirm={confirmDelete}
        testId="delete-user-confirm"
      />
    </div>
  );
};
